import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { formatDate, formatCurrency, getPaymentStatusLabel, getPaymentStatusColor, isPaymentPaid } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/common/Alert';
import CompanionModal from '../../components/CompanionModal';
import TripWizardModal from '../../components/TripWizard/TripWizardModal';
import QRCodeModal from '../../components/QRCodeModal';
import { Companion } from '../../types';
import { 
  Plane, 
  Users, 
  CreditCard, 
  Calendar, 
  PlusCircle, 
  Eye,
  Clock,
  MapPin,
  TrendingUp,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Star,
  Compass,
  Edit,
  Trash2,
  QrCode,
  UserPlus
} from 'lucide-react';

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { 
    trips, 
    companions, 
    currentTrip, 
    pastTrips,
    loading,
    error,
    refreshTrips,
    refreshCompanions,
    deleteCompanion
  } = useApp();

  // Modal states
  const [isCompanionModalOpen, setIsCompanionModalOpen] = useState(false);
  const [selectedCompanion, setSelectedCompanion] = useState<Companion | null>(null);
  const [companionModalMode, setCompanionModalMode] = useState<'create' | 'edit'>('create');
  const [isTripWizardOpen, setIsTripWizardOpen] = useState(false);
  const [qrCodeModal, setQrCodeModal] = useState<{
    isOpen: boolean;
    voucherCode: string;
    participantName?: string;
  }>({
    isOpen: false,
    voucherCode: '',
    participantName: ''
  });

  console.log('🏠 Dashboard: Rendering with state:', {
    user: user?.name,
    tripsCount: trips?.length || 0,
    companionsCount: companions?.length || 0,
    currentTrip: currentTrip?.id,
    pastTripsCount: pastTrips?.length || 0,
    loading,
    error
  });

  // Safe data access with fallbacks
  const safeTrips = trips || [];
  const safeCompanions = companions || [];

  const upcomingTrips = safeTrips.filter(trip => 
    new Date(trip.start_date) > new Date()
  ).slice(0, 3);

  // Filter recent trips excluding current trip to avoid duplication
  const recentTrips = safeTrips
    .filter(trip => {
      // Exclude current trip if it exists
      if (currentTrip && trip.id === currentTrip.id) return false;
      return true;
    })
    .sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime())
    .slice(0, 4); // Show up to 4 recent trips
  const pendingPayments = safeTrips.filter(trip => 
    !isPaymentPaid(trip.payment_status_id)
  ).length;

  const paidTrips = safeTrips.filter(trip => 
    isPaymentPaid(trip.payment_status_id)
  ).length;

  // Function to get trip status based on dates
  const getTripStatus = (trip: any) => {
    const now = new Date();
    const startDate = new Date(trip.start_date);
    const endDate = new Date(trip.end_date);
    
    if (startDate <= now && now <= endDate) {
      return { status: 'atual', label: 'Em andamento', color: 'bg-blue-100 text-blue-800', icon: Clock, bgGradient: 'from-blue-50 to-blue-100' };
    } else if (endDate < now) {
      return { status: 'realizada', label: 'Realizada', color: 'bg-gray-100 text-gray-800', icon: CheckCircle, bgGradient: 'from-gray-50 to-blue-50' };
    } else {
      return { status: 'futura', label: 'Próxima', color: 'bg-green-100 text-green-800', icon: Calendar, bgGradient: 'from-green-50 to-blue-50' };
    }
  };

  // Companion management functions
  const handleCreateCompanion = () => {
    setSelectedCompanion(null);
    setCompanionModalMode('create');
    setIsCompanionModalOpen(true);
  };

  const handleEditCompanion = (companion: Companion) => {
    setSelectedCompanion(companion);
    setCompanionModalMode('edit');
    setIsCompanionModalOpen(true);
  };

  const handleDeleteCompanion = async (companionId: number) => {
    if (window.confirm('Tem certeza que deseja remover este acompanhante?')) {
      try {
        await deleteCompanion(companionId);
      } catch (error) {
        console.error('Error deleting companion:', error);
        // Toast notification could be added here
      }
    }
  };

  const handleCloseCompanionModal = () => {
    setIsCompanionModalOpen(false);
    setSelectedCompanion(null);
  };

  const handleViewDetails = (tripId: number) => {
    navigate(`/trips/${tripId}`);
  };

  const handleViewQRCode = (voucherCode: string, participantName?: string) => {
    setQrCodeModal({
      isOpen: true,
      voucherCode,
      participantName
    });
  };

  // Show loading state only if both are loading
  if (loading.trips || loading.companions) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Hero Section with Welcome Message */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 opacity-90" />
        <div className="absolute inset-0 opacity-20">
          <div className="w-full h-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat'
          }} />
        </div>
        
        <div className="relative px-4 sm:px-6 py-8 sm:py-12 lg:py-16">
          <div className="mx-auto max-w-7xl">
            <div className="text-center">
              <div className="mb-4">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-white/10 backdrop-blur-sm rounded-full mb-4">
                  <Compass className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
              </div>
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
                Olá, {user?.name}! ✈️
              </h1>
              <p className="mt-2 sm:mt-4 text-base sm:text-xl text-blue-100 max-w-3xl mx-auto px-4">
                Bem-vindo ao seu portal exclusivo de Jericoacoara. Organize suas viagens para o paraíso nordestino com facilidade e praticidade.
              </p>
              <div className="mt-6 sm:mt-8">
                <button
                  onClick={() => setIsTripWizardOpen(true)}
                  className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 border border-transparent text-sm sm:text-base font-medium rounded-full text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  <PlusCircle className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  Planejar Nova Viagem
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-6 sm:-mt-8 relative z-10">
        {/* Error Display */}
        {(error.trips || error.companions) && (
          <div className="mb-8">
            <Alert type="warning">
              {error.trips && <div>Erro ao carregar viagens: {error.trips}</div>}
              {error.companions && <div>Erro ao carregar acompanhantes: {error.companions}</div>}
              <div className="mt-2 text-sm">
                Alguns dados podem não estar atualizados. Tente recarregar a página.
              </div>
            </Alert>
          </div>
        )}

        {/* Current Trip (if exists) */}
        {currentTrip && (
          <div className="mb-12 relative">
            {/* Background decoration */}
            <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/20 via-indigo-500/20 to-purple-500/20 rounded-2xl blur-lg opacity-60"></div>
            
            <div className="relative bg-white rounded-2xl shadow-2xl border border-blue-200/50 overflow-hidden">
              {/* Header with gradient */}
              <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-6">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center">
                      <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl mr-4 ring-2 ring-white/30">
                        <Clock className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">Viagem Atual</h2>
                        <p className="text-blue-100 text-sm">Em andamento agora</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium border border-white/30">
                        Ativo
                      </span>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <h3 className="font-bold text-3xl text-white mb-2">{currentTrip.hotel_name}</h3>
                    <div className="flex items-center text-blue-100">
                      <MapPin className="h-4 w-4 mr-2" />
                      <span>{currentTrip.hotel_name}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content section */}
              <div className="p-6 bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/30">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Period Card */}
                  <div className="group hover:scale-105 transition-all duration-300">
                    <div className="bg-white p-4 rounded-xl shadow-md border border-blue-100 hover:border-blue-300 hover:shadow-lg">
                      <div className="flex items-center mb-3">
                        <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg mr-3 group-hover:scale-110 transition-transform">
                          <Calendar className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Período</p>
                          <p className="text-lg font-bold text-gray-900">{currentTrip.total_days} dias</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-600">
                          {formatDate(currentTrip.start_date)} - {formatDate(currentTrip.end_date)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Participants Card */}
                  <div className="group hover:scale-105 transition-all duration-300">
                    <div className="bg-white p-4 rounded-xl shadow-md border border-emerald-100 hover:border-emerald-300 hover:shadow-lg">
                      <div className="flex items-center mb-3">
                        <div className="p-2 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg mr-3 group-hover:scale-110 transition-transform">
                          <Users className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Viajantes</p>
                          <p className="text-lg font-bold text-gray-900">{currentTrip.participants.length}</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          isPaymentPaid(currentTrip.payment_status_id) 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-orange-100 text-orange-800'
                        }`}>
                          {isPaymentPaid(currentTrip.payment_status_id) ? (
                            <>
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Pago
                            </>
                          ) : (
                            <>
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Pendente
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0 sm:space-x-4 pt-6 border-t border-gray-200">
                  <div className="flex items-center space-x-3">
                    {isPaymentPaid(currentTrip.payment_status_id) && currentTrip.voucher_code && (
                      <button
                        onClick={() => handleViewQRCode(currentTrip.voucher_code || '', currentTrip.user?.name)}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-medium hover:from-green-700 hover:to-emerald-700 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                      >
                        <QrCode className="h-5 w-5 mr-2" />
                        Ver QR Codes
                      </button>
                    )}
                  </div>
                  
                  <Link
                    to={`/trips/${currentTrip.id}`}
                    className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                  >
                    Ver todos os detalhes
                    <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Quick Actions */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <Star className="h-5 w-5 text-yellow-500 mr-2" />
                Ações Rápidas
              </h2>
              <div className="space-y-4">
                <button
                  onClick={() => setIsTripWizardOpen(true)}
                  className="flex items-center p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100 hover:border-blue-200 transition-all duration-200 group hover:shadow-md w-full text-left"
                >
                  <div className="p-2 bg-blue-500 rounded-lg group-hover:bg-blue-600 transition-colors">
                    <PlusCircle className="h-5 w-5 text-white" />
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="font-semibold text-gray-900 group-hover:text-blue-700">Nova Viagem</h3>
                    <p className="text-sm text-gray-600">Planeje seu próximo destino</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                </button>

                <Link
                  to="/companions"
                  className="flex items-center p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border border-emerald-100 hover:border-emerald-200 transition-all duration-200 group hover:shadow-md"
                >
                  <div className="p-2 bg-emerald-500 rounded-lg group-hover:bg-emerald-600 transition-colors">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="font-semibold text-gray-900 group-hover:text-emerald-700">Acompanhantes</h3>
                    <p className="text-sm text-gray-600">Gerencie quem vai viajar</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all" />
                </Link>
              </div>
            </div>
          </div>

          {/* Recent Trips */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                  <Plane className="h-5 w-5 text-blue-500 mr-2" />
                  Suas Viagens
                </h2>
                <Link
                  to="/trips"
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center group"
                >
                  Ver todas
                  <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

              {recentTrips.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Plane className="h-12 w-12 text-blue-500" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma viagem ainda</h3>
                  <p className="text-gray-600 mb-6">Comece planejando sua primeira aventura!</p>
                  <Link
                    to="/trips/new"
                    className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-full text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
                  >
                    <PlusCircle className="h-4 w-4 mr-2" />
                    Criar primeira viagem
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {recentTrips.map((trip) => {
                    const statusInfo = getTripStatus(trip);
                    const StatusIcon = statusInfo.icon;
                    
                    return (
                      <div
                        key={trip.id}
                        className={`bg-gradient-to-br ${statusInfo.bgGradient} rounded-xl p-6 border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300 group`}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="p-2 bg-gradient-to-br from-gray-500 to-blue-500 rounded-lg">
                            <MapPin className="h-5 w-5 text-white" />
                          </div>
                          <span className={`text-xs font-medium px-3 py-1 rounded-full ${statusInfo.color} flex items-center`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusInfo.label}
                          </span>
                        </div>
                        <h3 className="font-bold text-gray-900 text-lg mb-2 group-hover:text-blue-700 transition-colors">
                          {trip.hotel_name}
                        </h3>
                        <div className="space-y-2 text-sm text-gray-600 mb-4">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-2" />
                            {formatDate(trip.start_date)} - {formatDate(trip.end_date)}
                          </div>
                          <div className="flex items-center">
                            <Users className="h-4 w-4 mr-2" />
                            {trip.participants.length} participante{trip.participants.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-gray-900">
                            {formatCurrency(trip.total_amount)}
                          </span>
                          <div className="flex items-center space-x-2">
                            {isPaymentPaid(trip.payment_status_id) && trip.voucher_code && (
                              <button
                                onClick={() => handleViewQRCode(trip.voucher_code || '', trip.user?.name)}
                                className="inline-flex items-center px-3 py-1 text-sm font-medium text-green-700 bg-green-100 rounded-full hover:bg-green-200 transition-colors group"
                                title="Ver QR Codes"
                              >
                                <QrCode className="h-3 w-3 mr-1" />
                                QR
                              </button>
                            )}
                            <Link
                              to={`/trips/${trip.id}`}
                              className="inline-flex items-center px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors group"
                            >
                              Ver detalhes
                              <ArrowRight className="h-3 w-3 ml-1 group-hover:translate-x-1 transition-transform" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Upcoming Trips Section */}
        {upcomingTrips.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-12">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <Clock className="h-5 w-5 text-orange-500 mr-2" />
              Próximas Viagens
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingTrips.map((trip) => (
                <div
                  key={trip.id}
                  className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-100 hover:border-orange-200 hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg">
                      <MapPin className="h-5 w-5 text-white" />
                    </div>
                    <span className="text-xs font-medium text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                      Em breve
                    </span>
                  </div>
                  <h3 className="font-bold text-gray-900 text-lg mb-2 group-hover:text-orange-700 transition-colors">
                    {trip.hotel_name}
                  </h3>
                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      {formatDate(trip.start_date)} - {formatDate(trip.end_date)}
                    </div>
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-2" />
                      {trip.participants.length} participante{trip.participants.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-gray-900">
                      {formatCurrency(trip.total_amount)}
                    </span>
                    <div className="flex items-center space-x-2">
                      {isPaymentPaid(trip.payment_status_id) && trip.voucher_code && (
                        <button
                          onClick={() => handleViewQRCode(trip.voucher_code || '', trip.user?.name)}
                          className="inline-flex items-center px-3 py-1 text-sm font-medium text-green-700 bg-green-100 rounded-full hover:bg-green-200 transition-colors group"
                          title="Ver QR Codes"
                        >
                          <QrCode className="h-3 w-3 mr-1" />
                          QR
                        </button>
                      )}
                      <Link
                        to={`/trips/${trip.id}`}
                        className="inline-flex items-center px-3 py-1 text-sm font-medium text-orange-700 bg-orange-100 rounded-full hover:bg-orange-200 transition-colors group"
                      >
                        Ver detalhes
                        <ArrowRight className="h-3 w-3 ml-1 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Companions Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-12">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <Users className="h-5 w-5 text-emerald-500 mr-2" />
              Meus Acompanhantes
            </h2>
            <button
              onClick={handleCreateCompanion}
              className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-all duration-200 transform hover:scale-105"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Adicionar Acompanhante
            </button>
          </div>

          {safeCompanions.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-gradient-to-br from-emerald-100 to-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-12 w-12 text-emerald-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum acompanhante cadastrado</h3>
              <p className="text-gray-600 mb-6">Adicione acompanhantes para facilitar futuras viagens!</p>
              <button
                onClick={handleCreateCompanion}
                className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-full text-white bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Adicionar primeiro acompanhante
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {safeCompanions.map((companion) => (
                <div
                  key={companion.id}
                  className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-6 border border-emerald-100 hover:border-emerald-200 hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2 bg-gradient-to-br from-emerald-500 to-green-500 rounded-lg">
                      <Users className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleEditCompanion(companion)}
                        className="p-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100 rounded-lg transition-all duration-200"
                        title="Editar acompanhante"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCompanion(companion.id)}
                        className="p-2 text-red-600 hover:text-red-700 hover:bg-red-100 rounded-lg transition-all duration-200"
                        title="Remover acompanhante"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <h3 className="font-bold text-gray-900 text-lg mb-2 group-hover:text-emerald-700 transition-colors">
                    {companion.name}
                  </h3>
                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <div className="flex items-center">
                      <span className="text-gray-500">Data de nascimento:</span>
                      <span className="ml-2">{formatDate(companion.date_of_birth)}</span>
                    </div>
                    {companion.email && (
                      <div className="flex items-center">
                        <span className="text-gray-500">Email:</span>
                        <span className="ml-2 truncate">{companion.email}</span>
                      </div>
                    )}
                    <div className="flex items-center">
                      <span className="text-gray-500">CPF:</span>
                      <span className="ml-2">{companion.document_number}</span>
                    </div>
                    {companion.phone && (
                      <div className="flex items-center">
                        <span className="text-gray-500">Telefone:</span>
                        <span className="ml-2">{companion.country_code || '+55'} {companion.phone}</span>
                      </div>
                    )}
                    {companion.age && (
                      <div className="flex items-center">
                        <span className="text-gray-500">Idade:</span>
                        <span className="ml-2">{companion.age} anos</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Companion Modal */}
      <CompanionModal
        isOpen={isCompanionModalOpen}
        onClose={handleCloseCompanionModal}
        companion={selectedCompanion}
        mode={companionModalMode}
      />

      {/* Trip Wizard Modal */}
      <TripWizardModal
        isOpen={isTripWizardOpen}
        onClose={() => setIsTripWizardOpen(false)}
        onSuccess={(tripId) => {
          console.log('✅ Trip created successfully with ID:', tripId);
          refreshTrips();
          setIsTripWizardOpen(false);
        }}
      />

      {/* QR Code Modal */}
      <QRCodeModal
        isOpen={qrCodeModal.isOpen}
        onClose={() => setQrCodeModal({ isOpen: false, voucherCode: '', participantName: '' })}
        voucherCode={qrCodeModal.voucherCode}
        participantName={qrCodeModal.participantName}
      />
    </div>
  );
};

export default DashboardPage;
